import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
} from '@mui/material';

import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
  darkPanelSx,
  eyebrowSx,
  heroPanelSx,
  iconBadgeSx,
  pageHeadlineSx,
  subtleCopySx,
} from './designSystem';
import { FONT_DISPLAY } from './theme';

const setupSteps = [
  {
    icon: <DownloadForOfflineOutlinedIcon />,
    accent: '#13947F',
    tint: 'rgba(19,148,127,0.12)',
    title: 'Pick the pilot environment',
    body: 'Start with one classroom, lab, or intervention environment where the success criteria are easy to observe.',
  },
  {
    icon: <AccountTreeOutlinedIcon />,
    accent: '#46729B',
    tint: 'rgba(70,114,155,0.12)',
    title: 'Set the school baseline',
    body: 'Write the default review rules and hard overrides you want every enrolled environment to inherit.',
  },
  {
    icon: <SettingsSuggestOutlinedIcon />,
    accent: '#C08A2D',
    tint: 'rgba(192,138,45,0.12)',
    title: 'Add one profile if needed',
    body: 'Only create an extra classroom or program profile if the pilot environment needs different rules than the district baseline.',
  },
  {
    icon: <VerifiedUserOutlinedIcon />,
    accent: '#0B2E43',
    tint: 'rgba(11,46,67,0.08)',
    title: 'Enroll devices and watch the fit',
    body: 'Enroll the pilot devices, review what gets allowed or blocked, and tighten the policy before expanding further.',
  },
];

function SetupGuide() {
  return (
    <Container sx={{ py: { xs: 6, md: 8 } }}>
      <Stack spacing={5}>
        <Box sx={{ ...heroPanelSx, p: { xs: 3.5, md: 6 } }}>
          <Typography variant="overline" sx={eyebrowSx}>
            Rollout Guide
          </Typography>
          <Typography variant="h2" sx={{ ...pageHeadlineSx, mt: 1.5, maxWidth: 760 }}>
            A rollout plan school teams can actually follow.
          </Typography>
          <Typography sx={{ ...subtleCopySx, mt: 2, maxWidth: 620, fontSize: '1.05rem' }}>
            Four steps from first install to a confident, policy-fit deployment.
          </Typography>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              left: { xs: 27, md: 33 },
              top: 36,
              bottom: 36,
              width: 2,
              background: 'linear-gradient(180deg, rgba(19,148,127,0.35), rgba(11,46,67,0.12))',
              display: { xs: 'block' },
            }}
          />
          <Stack spacing={2.5}>
            {setupSteps.map((step, index) => (
              <Card
                key={step.title}
                sx={{
                  borderRadius: '18px',
                  position: 'relative',
                  transition: 'transform 200ms ease, box-shadow 200ms ease',
                  '&:hover': {
                    transform: 'translateX(4px)',
                    boxShadow: '0 2px 4px rgba(11,46,67,0.05), 0 24px 48px -24px rgba(11,46,67,0.2)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3.25 } }}>
                  <Stack direction="row" spacing={{ xs: 2, md: 3 }} alignItems="flex-start">
                    <Box sx={{ ...iconBadgeSx(step.tint, step.accent), width: { xs: 54, md: 66 }, height: { xs: 54, md: 66 }, borderRadius: '18px', zIndex: 1 }}>
                      {step.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: FONT_DISPLAY,
                          color: step.accent,
                          fontWeight: 800,
                          fontSize: '0.7rem',
                          letterSpacing: '0.14em',
                          textTransform: 'uppercase',
                          mb: 0.5,
                        }}
                      >
                        Step {index + 1}
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ ...subtleCopySx, maxWidth: 640 }}>{step.body}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>

        <Card sx={darkPanelSx}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>
              Ready for a strong first pilot?
            </Typography>
            <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.66)', lineHeight: 1.75, maxWidth: 560 }}>
              Install the extension on a test device, then reach out — we help schools design the first policy and pick the right rollout path.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.75} sx={{ mt: 3.5 }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                href="https://chromewebstore.google.com/detail/blockwebai/jgfdcafnckmhficckccgcniebflbiimi"
                target="_blank"
                rel="noopener noreferrer"
              >
                Install Extension
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.32)',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)' },
                }}
                href="mailto:jonathanlarsonbusiness@gmail.com"
              >
                Contact for Pilot Support
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}

export default SetupGuide;
